import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RUN_ANSWER_FAILED, RUN_TAKEOVER_FAILED } from '../../constants/error-messages';
import {
  TAKEOVER_FRAME_FAILURES_ALLOWED,
  TAKEOVER_FRAME_MS,
  TAKEOVER_LEAVE_STAGE_KEY,
  TAKEOVER_PAGE_VIEWPORT,
  TAKEOVER_QUICK_KEYS,
} from '../../constants/takeover';
import {
  isLeaveStageKey,
  keyInput,
  sendingStatus,
  statusOf,
  takeoverIsOver,
  useTakeoverInput,
} from '../../hooks/useTakeoverInput';
import { useToast } from '../../hooks/useToast';
import { apiClient } from '../../lib/api';
import { ProjectRun, RunAction, RunQuestion } from '../../types/pipeline';
import { formatRelativeTime } from '../../utils/dateUtils';
import { getErrorDetail } from '../../utils/errorUtils';
import { Badge, Spinner } from '../ui';

/** SKY-447: per-project agent-run history + artifacts + page cost (operator view). */

const STATUS_VARIANT: Record<string, 'success' | 'error' | 'primary' | 'neutral'> = {
  succeeded: 'success',
  failed: 'error',
  running: 'primary',
  queued: 'neutral',
  cancelled: 'neutral',
};

const RUNS_LIMIT = 200;

type VerdictTone = 'success' | 'error' | 'warning' | 'primary-light' | 'neutral';

const VERDICT_VARIANT: Record<string, VerdictTone> = {
  done: 'success',
  public: 'success',
  found: 'success',
  partial: 'primary-light',
  continue: 'primary-light',
  gated: 'warning',
  'needs-human': 'warning',
  unreadable: 'error',
  failed: 'error',
  unknown: 'neutral',
};

const verdictField = (data: Record<string, unknown> | undefined, key: string): string | null => {
  const value = data?.[key];
  if (value === undefined || value === null || value === '') return null;
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

/** The DATA fields worth a glance (count, next, requirement, host, then the reason) as one line. */
const verdictSummary = (data: Record<string, unknown> | undefined): string =>
  [
    ...['count', 'next', 'requirement', 'host'].map((key) => {
      const value = verdictField(data, key);
      return value ? `${key} ${value}` : null;
    }),
    verdictField(data, 'reason'),
  ]
    .filter(Boolean)
    .join(' · ');

const runName = (run: ProjectRun): string => run.task ?? run.workload;

/** Artifact uris are agent-written loose text — only render web links. */
const safeHref = (uri: string | null): string | null =>
  uri && /^https?:\/\//i.test(uri) ? uri : null;

/**
 * A lead_list artifact's uri is a bare lead_lists UUID, so safeHref refuses it
 * and the operator's only proof of a sourced list rendered as inert text
 * (SKY-552). Route it to the review surface that exists today — the campaign
 * audience tab, which works for un-campaigned projects. SKY-544 replaces this
 * with a native operator list view.
 */
const artifactHref = (kind: string, uri: string | null, projectId: string): string | null => {
  const web = safeHref(uri);
  if (web) return web;
  if (kind === 'lead_list' && uri) return `/portal/campaigns/${projectId}?tab=audience`;
  return null;
};


/**
 * SKY-461 Layer 2 (PR6): Langfuse trace deep-link, derived — trace-id is
 * run_id without dashes, so no backend change and it works retroactively.
 * Hidden unless VITE_LANGFUSE_URL is set (eng builds only; the UI is reached
 * via the SSM tunnel, so this is typically http://localhost:3000).
 */
const RAW_LANGFUSE_URL = (import.meta.env.VITE_LANGFUSE_URL as string | undefined) ?? '';
// Same href discipline as safeHref above: only http(s) values render as links.
const LANGFUSE_URL = /^https?:\/\//i.test(RAW_LANGFUSE_URL) ? RAW_LANGFUSE_URL.replace(/\/+$/, '') : '';
const LANGFUSE_PROJECT = (import.meta.env.VITE_LANGFUSE_PROJECT as string | undefined) ?? 'skylarq-dev';
const traceUrl = (runId: string): string | null =>
  LANGFUSE_URL ? `${LANGFUSE_URL}/project/${LANGFUSE_PROJECT}/traces/${runId.replace(/-/g, '')}` : null;

const when = (r: ProjectRun) => {
  const ts = r.completed_at ?? r.started_at ?? r.created_at;
  return ts ? formatRelativeTime(ts) : '—';
};

/* ---- SKY-490 pure helpers (exported for future unit tests) ---------------- */

/** Wall-clock run time. Either timestamp missing (still running / never started)
 *  reads as unknown rather than a misleading zero. */
export const formatDuration = (startedAt: string | null, completedAt: string | null): string => {
  if (!startedAt || !completedAt) return '—';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const total = Math.round(ms / 1000);
  const [h, m, s] = [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60];
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
};

const asNum = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * Token counts as the skill-run activity writes them: usage_metadata.usage is the
 * Claude Code CLI's flat snake_case block; model_usage is the per-model camelCase
 * breakdown, summed only when the flat block is absent.
 */
export const extractTokens = (usage: Record<string, unknown>): { input: number; output: number } => {
  const flat = usage?.usage as Record<string, unknown> | undefined;
  if (flat && (typeof flat.input_tokens === 'number' || typeof flat.output_tokens === 'number')) {
    // Cache tokens carry the bulk of CLI input (#623 review P1): input_tokens
    // alone would drastically underreport (e.g. "4 in" on a 150k-token run).
    return {
      input:
        asNum(flat.input_tokens) +
        asNum(flat.cache_read_input_tokens) +
        asNum(flat.cache_creation_input_tokens),
      output: asNum(flat.output_tokens),
    };
  }
  const perModel = usage?.model_usage as Record<string, Record<string, unknown>> | undefined;
  if (perModel && typeof perModel === 'object') {
    return Object.values(perModel).reduce<{ input: number; output: number }>(
      (acc, mu) => ({
        input:
          acc.input +
          asNum(mu?.inputTokens) +
          asNum(mu?.cacheReadInputTokens) +
          asNum(mu?.cacheCreationInputTokens),
        output: acc.output + asNum(mu?.outputTokens),
      }),
      { input: 0, output: 0 }
    );
  }
  return { input: 0, output: 0 };
};

const Meta: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <dt className="text-2xs uppercase tracking-wide text-neutral-500">{label}</dt>
    <dd className="text-xs text-neutral-900 break-words">{value}</dd>
  </div>
);

const Block: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-2xs uppercase tracking-wide text-neutral-500 mb-0.5">{label}</p>
    {children}
  </div>
);

const ActionLine: React.FC<{ action: RunAction }> = ({ action }) => (
  <li className="text-xs text-neutral-700 break-words">
    <span className="font-medium text-neutral-900">{action.tool}</span>
    {action.args_keys.length > 0 && (
      <span className="text-neutral-500"> → {action.args_keys.join(', ')}</span>
    )}
    <span className={action.status === 'error' ? 'text-error-600 ml-1' : 'text-neutral-500 ml-1'}>
      ({action.status}
      {action.error ? `: ${action.error}` : ''})
    </span>
  </li>
);

const RunDetail: React.FC<{ run: ProjectRun }> = ({ run }) => {
  const tokens = extractTokens(run.usage_metadata ?? {});
  const cost = asNum(run.usage_metadata?.total_cost_usd);
  // Rolling-deploy skew: an old backend may serve runs without the new fields for
  // a moment — tolerate their absence rather than crashing the panel.
  const actions = run.actions ?? [];
  return (
    <div id={`run-detail-${run.id}`} className="mt-2 pt-2 border-t border-neutral-200 space-y-2">
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1">
        <Meta label="Duration" value={formatDuration(run.started_at, run.completed_at)} />
        <Meta label="Model" value={run.model ?? '—'} />
        <Meta
          label="Tokens"
          value={
            tokens.input || tokens.output
              ? `${tokens.input.toLocaleString()} in / ${tokens.output.toLocaleString()} out`
              : '—'
          }
        />
        <Meta label="Cost" value={cost ? `$${cost.toFixed(4)}` : '—'} />
      </dl>
      {run.input_reference && (
        <Block label="Input">
          <p className="text-xs font-mono text-neutral-700 whitespace-pre-wrap break-words bg-neutral-50 rounded p-2 max-h-40 overflow-y-auto">
            {run.input_reference}
          </p>
          {run.input_truncated && (
            <p className="text-2xs text-neutral-500 mt-0.5">Preview truncated.</p>
          )}
        </Block>
      )}
      {run.output_reference && (
        <Block label="Output">
          <p className="text-xs font-mono text-neutral-700 whitespace-pre-wrap break-words bg-neutral-50 rounded p-2 max-h-40 overflow-y-auto">
            {run.output_reference}
          </p>
          {run.output_truncated && (
            <p className="text-2xs text-neutral-500 mt-0.5">Preview truncated — open the trace for the full output.</p>
          )}
        </Block>
      )}
      {actions.length > 0 && (
        <Block label={`Actions (${actions.length})`}>
          <ul className="space-y-0.5">
            {actions.map((a, i) => (
              <ActionLine key={a.id ?? `${run.id}-act-${i}`} action={a} />
            ))}
          </ul>
        </Block>
      )}
    </div>
  );
};

const ANSWER_HINT: Record<RunQuestion['kind'], string> = {
  text: 'The agent reads your answer and carries on.',
  secret: 'Typed into the page for the agent; the agent never sees it.',
  link: 'Paste the full link; the agent opens it.',
};

const ANSWER_INPUT_TYPE: Record<RunQuestion['kind'], string> = {
  text: 'text',
  secret: 'password',
  link: 'url',
};

const pageStillOpen = (question: RunQuestion): boolean =>
  !!question.browser_held_until && new Date(question.browser_held_until).getTime() > Date.now();

/** Live view of the held page: click, type and scroll on the real page, then hand back. */
const TakeoverPanel: React.FC<{ run: ProjectRun; onDone: () => void; onClose: () => void }> = ({
  run,
  onDone,
  onClose,
}) => {
  const { showToast } = useToast();
  const [frame, setFrame] = useState<string | null>(null);
  const [pageUrl, setPageUrl] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const frameRef = useRef<string | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const onRefused = useCallback(
    (error: unknown) => {
      console.error('Takeover input refused:', error);
      showToast(RUN_TAKEOVER_FAILED, 'error');
      if (takeoverIsOver(error)) onClose();
    },
    [showToast, onClose],
  );
  const input = useTakeoverInput({ runId: run.id, onUrl: setPageUrl, onRefused });

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    const tick = async () => {
      try {
        const shot = await apiClient.getTakeoverFrame(run.id);
        if (stopped) return;
        failures = 0;
        const next = URL.createObjectURL(shot.jpeg);
        if (frameRef.current) URL.revokeObjectURL(frameRef.current);
        frameRef.current = next;
        setFrame(next);
        if (shot.url) setPageUrl(shot.url);
      } catch (error) {
        if (stopped) return;
        console.error('Takeover frame failed:', error);
        failures += 1;
        if (takeoverIsOver(error) || failures >= TAKEOVER_FRAME_FAILURES_ALLOWED) {
          showToast(RUN_TAKEOVER_FAILED, 'error');
          onClose();
          return;
        }
      }
      timer = setTimeout(tick, TAKEOVER_FRAME_MS);
    };
    tick();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (frameRef.current) URL.revokeObjectURL(frameRef.current);
    };
  }, [run.id, onClose, showToast]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isLeaveStageKey(e)) {
      e.preventDefault();
      document.getElementById(`run-takeover-done-${run.id}`)?.focus();
      return;
    }
    if (busy) return;
    const next = keyInput(e);
    if (!next) return;
    e.preventDefault();
    input.send(next);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const text = e.clipboardData.getData('text');
    if (!text || busy) return;
    e.preventDefault();
    input.typeText(text);
  };

  const clickAt = (e: React.MouseEvent<HTMLImageElement>) => {
    const img = imgRef.current;
    if (!img || busy) return;
    const box = img.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * TAKEOVER_PAGE_VIEWPORT.width;
    const y = ((e.clientY - box.top) / box.height) * TAKEOVER_PAGE_VIEWPORT.height;
    stageRef.current?.focus();
    input.send({ action: 'click', x: Math.round(x), y: Math.round(y) });
  };

  const finish = async () => {
    setBusy(true);
    try {
      const finished = await apiClient.finishTakeover(run.id);
      const connected = finished.status === 'connected';
      showToast(
        connected ? 'Connected — the sign-in was kept' : 'Handed back — a follow-up run is starting',
        'success',
      );
      onDone();
    } catch (error) {
      console.error('Takeover finish failed:', error);
      const notYet = statusOf(error) === 422;
      showToast(
        notYet ? getErrorDetail(error, RUN_TAKEOVER_FAILED) : RUN_TAKEOVER_FAILED,
        notYet ? 'warning' : 'error',
      );
      setBusy(false);
    }
  };

  return (
    <div id={`run-takeover-${run.id}`} className="mt-2 space-y-2">
      <p
        className="text-2xs text-neutral-600"
        role="status"
        aria-live={input.retrying || input.stalled ? 'polite' : 'off'}
      >
        {sendingStatus(input) ??
          'You are on the page. Click it, then type as you normally would; paste works too.'}
      </p>
      {pageUrl && <p className="text-2xs text-neutral-600 break-all">{pageUrl}</p>}
      {frame ? (
        <div
          ref={stageRef}
          id={`run-takeover-stage-${run.id}`}
          role="application"
          aria-label={`Live view of the held page: click it, then type to send keys to the page. Press ${TAKEOVER_LEAVE_STAGE_KEY} to leave it.`}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          className="rounded focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-40"
        >
          <img
            ref={imgRef}
            src={frame}
            alt="Live view of the held page"
            onClick={clickAt}
            onWheel={(e) => !busy && input.send({ action: 'scroll', dy: Math.round(e.deltaY) })}
            className="w-full max-w-full rounded border border-neutral-300 cursor-crosshair select-none"
            draggable={false}
          />
        </div>
      ) : (
        <Spinner size="sm" />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`run-takeover-text-${run.id}`} className="flex-1 min-w-0">
          <span className="sr-only">Text to type into the page</span>
          <input
            id={`run-takeover-text-${run.id}`}
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Text to type into the focused field"
            autoComplete="off"
            className="w-full text-sm border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
          />
        </label>
        <button
          type="button"
          id={`run-takeover-type-${run.id}`}
          disabled={busy || !typed}
          onClick={() => {
            input.typeText(typed);
            setTyped('');
          }}
          className="text-sm px-2 py-1 rounded border border-neutral-300 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
        >
          Type
        </button>
        {TAKEOVER_QUICK_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            id={`run-takeover-key-${key.toLowerCase()}-${run.id}`}
            disabled={busy}
            aria-label={`Press the ${key} key on the page`}
            onClick={() => input.send({ action: 'press', key })}
            className="text-sm px-2 py-1 rounded border border-neutral-300 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          id={`run-takeover-done-${run.id}`}
          disabled={busy || input.pending}
          onClick={() => void finish()}
          className="text-sm px-3 py-1 rounded bg-primary-600 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
        >
          Done, hand back
        </button>
        <button
          type="button"
          id={`run-takeover-close-${run.id}`}
          onClick={onClose}
          className="text-sm px-2 py-1 text-neutral-600 hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10 rounded"
        >
          Back to the answer box
        </button>
      </div>
    </div>
  );
};

/** A run stopped to ask something only a person can supply; this is where they answer. */
const QuestionCard: React.FC<{ run: ProjectRun; onAnswered: () => void }> = ({ run, onAnswered }) => {
  const { showToast } = useToast();
  const question = run.question as RunQuestion;
  const [answer, setAnswer] = useState('');
  const [sending, setSending] = useState(false);
  const [takingOver, setTakingOver] = useState(false);
  const closeTakeover = useCallback(() => setTakingOver(false), []);
  const shot = run.artifacts.find((a) => a.id && a.id === question.screenshot_artifact_id);
  const shotHref = shot ? safeHref(shot.uri) : null;
  const inputId = `run-answer-${run.id}`;
  const open = pageStillOpen(question);

  const submitAnswer = async (value: string) => {
    if (!value.trim() || sending) return;
    setSending(true);
    try {
      await apiClient.answerRun(run.id, value.trim());
      showToast('Answer sent — a follow-up run is starting', 'success');
      setAnswer('');
      onAnswered();
    } catch (error) {
      console.error('Run answer failed:', error);
      showToast(RUN_ANSWER_FAILED, 'error');
    } finally {
      setSending(false);
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    void submitAnswer(answer);
  };

  return (
    <form
      id={`run-question-${run.id}`}
      onSubmit={submit}
      aria-labelledby={`run-question-text-${run.id}`}
      className="mt-2 border border-warning-300 bg-warning-50 rounded-md p-2 space-y-2"
    >
      <p id={`run-question-text-${run.id}`} className="text-sm text-neutral-900 break-words">
        <span className="font-medium">Needs you:</span> {question.text}
      </p>
      <p className="text-2xs text-neutral-600">
        {ANSWER_HINT[question.kind]}{' '}
        {open
          ? `The page it was asked on is still open until ${new Date(question.browser_held_until as string).toLocaleTimeString()}.`
          : 'The page it was asked on has closed; the follow-up run signs in again.'}
      </p>
      {takingOver && <TakeoverPanel run={run} onDone={onAnswered} onClose={closeTakeover} />}
      {!takingOver && shotHref && (
        <a href={shotHref} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={shotHref}
            alt={`Page the question was asked on${question.page_host ? `: ${question.page_host}` : ''}`}
            className="max-h-64 max-w-full rounded border border-neutral-200"
          />
        </a>
      )}
      {!!question.options?.length && (
        <div className="flex flex-wrap gap-1.5">
          {question.options.map((option) => (
            <button
              key={option}
              type="button"
              id={`run-answer-option-${run.id}-${option.replace(/\W+/g, '-')}`}
              disabled={sending}
              onClick={() => void submitAnswer(option)}
              className="text-sm px-2 py-1 rounded border border-neutral-300 hover:bg-neutral-50 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
            >
              {option}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <label htmlFor={inputId} className="flex-1 min-w-0">
          <span className="block text-2xs uppercase tracking-wide text-neutral-500 mb-0.5">
            {question.kind === 'secret' ? `Answer${question.field ? ` for "${question.field}"` : ''}` : 'Answer'}
          </span>
          <input
            id={inputId}
            type={ANSWER_INPUT_TYPE[question.kind]}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            autoComplete="off"
            maxLength={question.kind === 'secret' ? 200 : 2000}
            className="w-full text-sm border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
          />
        </label>
        <button
          type="submit"
          id={`run-answer-send-${run.id}`}
          disabled={sending || !answer.trim()}
          className="text-sm px-3 py-1 rounded bg-primary-600 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
        >
          {sending ? 'Sending…' : 'Send answer'}
        </button>
        {open && !takingOver && (
          <button
            type="button"
            id={`run-takeover-open-${run.id}`}
            onClick={() => setTakingOver(true)}
            className="text-sm px-3 py-1 rounded border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
          >
            Take over the page
          </button>
        )}
      </div>
    </form>
  );
};

const AnsweredLine: React.FC<{ question: RunQuestion }> = ({ question }) => (
  <p className="text-xs text-neutral-600 mt-1 break-words">
    Asked: {question.text} · answered
    {question.answered_at ? ` ${formatRelativeTime(question.answered_at)}` : ''}
    {question.answer_run_id ? `, continuing in run ${question.answer_run_id.slice(0, 8)}` : ''}
  </p>
);

type RunTree = { top: ProjectRun[]; nested: Map<string, ProjectRun[]>; orphans: ProjectRun[] };

/** Roots newest first, their runs nested oldest first; a child whose root is off the page is an orphan. */
const buildRunTree = (runs: ProjectRun[]): RunTree => {
  const rootIds = new Set(runs.filter((r) => !r.parent_run_id).map((r) => r.id));
  const nested = new Map<string, ProjectRun[]>();
  const top: ProjectRun[] = [];
  const orphans: ProjectRun[] = [];
  for (const r of runs) {
    if (!r.parent_run_id) top.push(r);
    else if (rootIds.has(r.parent_run_id)) nested.set(r.parent_run_id, [...(nested.get(r.parent_run_id) ?? []), r]);
    else orphans.push(r);
  }
  nested.forEach((list) =>
    list.sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))
  );
  return { top, nested, orphans };
};

const RunRow: React.FC<{
  run: ProjectRun;
  projectId: string;
  nestedRuns: ProjectRun[];
  expanded: Set<string>;
  toggle: (runId: string) => void;
  onOpenList?: (listId: string) => void;
  onAnswered: () => void;
}> = ({ run, projectId, nestedRuns, expanded, toggle, onOpenList, onAnswered }) => {
  const name = runName(run);
  const open = expanded.has(run.id);
  const summary = verdictSummary(run.verdict_data);
  const isPlanRoot = nestedRuns.length > 0;
  const trace = traceUrl(run.id);
  return (
    <li className="border border-neutral-200 rounded-md p-2">
      <div className="flex items-center gap-2 min-w-0">
        <button
          type="button"
          id={`run-toggle-${run.id}`}
          onClick={() => toggle(run.id)}
          aria-expanded={open}
          aria-controls={open ? `run-detail-${run.id}` : undefined}
          aria-label={`${open ? 'Hide' : 'Show'} details for ${name} run`}
          className="text-neutral-500 hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10 rounded"
        >
          <span aria-hidden="true" className="text-2xs">
            {open ? '▾' : '▸'}
          </span>
        </button>
        <Badge variant={STATUS_VARIANT[run.status] ?? 'neutral'} size="sm">
          {run.status}
        </Badge>
        <span className="text-sm font-medium text-neutral-900 whitespace-nowrap">{name}</span>
        {run.verdict && (
          <Badge variant={VERDICT_VARIANT[run.verdict] ?? 'neutral'} size="sm">
            {run.verdict}
          </Badge>
        )}
        {!isPlanRoot && summary && (
          <span className="flex-1 min-w-0 truncate text-xs text-neutral-500" title={summary}>
            {summary}
          </span>
        )}
        <span className="text-xs text-neutral-500 ml-auto whitespace-nowrap">{when(run)}</span>
        {trace && (
          <a
            href={trace}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary-600 hover:text-primary-700"
            aria-label={`View Langfuse trace for run ${run.id}`}
          >
            Trace
          </a>
        )}
      </div>
      {isPlanRoot && summary && (
        <p id={`run-plan-verdict-${run.id}`} className="text-xs text-neutral-700 mt-1 break-words">
          {summary}
        </p>
      )}
      {run.status === 'failed' && (
        <p className="text-xs text-error-600 mt-1 break-words" title={run.sanitized_error ?? undefined}>
          {run.error_code ?? 'Run failed'}
          {run.sanitized_error ? `: ${run.sanitized_error}` : ''}
        </p>
      )}
      {run.question &&
        (run.question.answered_at ? (
          <AnsweredLine question={run.question} />
        ) : (
          run.status === 'succeeded' && <QuestionCard run={run} onAnswered={onAnswered} />
        ))}
      {run.artifacts.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {run.artifacts.map((a, i) => {
            const href = artifactHref(a.kind, a.uri, projectId);
            const opensHere = a.kind === 'lead_list' && a.uri && onOpenList;
            return (
              <li key={a.id ?? `${run.id}-${i}`} className="text-xs text-neutral-700">
                <span className="uppercase text-2xs text-neutral-500 mr-1">{a.kind}</span>
                {opensHere ? (
                  <button
                    type="button"
                    id={`run-artifact-${run.id}-${i}`}
                    onClick={() => onOpenList(a.uri as string)}
                    aria-label={`Open ${a.kind} ${a.label}`}
                    className="text-primary-600 hover:underline text-left"
                  >
                    {a.label}
                  </button>
                ) : href ? (
                  <a
                    id={`run-artifact-${run.id}-${i}`}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    {a.label}
                  </a>
                ) : (
                  a.label
                )}
              </li>
            );
          })}
        </ul>
      )}
      {open && <RunDetail run={run} />}
      {isPlanRoot && (
        <ul
          id={`run-children-${run.id}`}
          aria-label={`Runs under ${name}`}
          className="mt-2 ml-4 pl-2 border-l-2 border-neutral-200 space-y-1.5"
        >
          {nestedRuns.map((child) => (
            <RunRow
              key={child.id}
              run={child}
              projectId={projectId}
              nestedRuns={[]}
              expanded={expanded}
              toggle={toggle}
              onOpenList={onOpenList}
              onAnswered={onAnswered}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

/** Runs that are still moving; the panel polls only while one of these exists. */
const IN_FLIGHT = new Set(['queued', 'running']);
const POLL_MS = 5000;
/** A run records its question before it holds the page, so the hold lands just after the
 * run reports done. Keep polling that gap, bounded because a question on a task that
 * never holds a page would otherwise poll forever. */
const SETTLE_MS = 60_000;

const awaitingHold = (run: ProjectRun): boolean => {
  const question = run.question;
  if (!question || question.answered_at || question.browser_held_until) return false;
  const done = run.completed_at ? Date.parse(run.completed_at) : NaN;
  return Number.isFinite(done) && Date.now() - done < SETTLE_MS;
};

export const ProjectRunsSection: React.FC<{
  projectId: string;
  refreshKey?: number;
  /** SKY-544: hand a lead_list artifact to the decision mirror instead of
   * bouncing the operator into the client portal. */
  onOpenList?: (listId: string) => void;
}> = ({ projectId, refreshKey = 0, onOpenList }) => {
  const [runs, setRuns] = useState<ProjectRun[]>([]);
  const [listedCost, setListedCost] = useState(0);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const tree = useMemo(() => buildRunTree(runs), [runs]);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  // Bumped on every project change and unmount, so a stale response cannot land.
  const genRef = useRef(0);

  const toggle = (runId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(runId)) next.add(runId);
      return next;
    });

  const load = useCallback(
    (gen: number) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      apiClient
        .getProjectRuns(projectId, { limit: RUNS_LIMIT })
        .then((res) => {
          if (genRef.current !== gen) return;
          setRuns(res.runs);
          setListedCost(res.listed_cost_usd);
          setState('ready');
          // A dispatched run takes minutes; without this the operator watches a
          // frozen panel and has to close/reopen to learn anything (SKY-552).
          // Polling stops the moment nothing is in flight — no idle traffic.
          if (res.runs.some((r) => IN_FLIGHT.has(r.status) || awaitingHold(r))) {
            timerRef.current = setTimeout(() => load(gen), POLL_MS);
          }
        })
        .catch((error) => {
          if (genRef.current !== gen) return;
          console.error('Project runs fetch failed:', error);
          // Stop polling on error rather than hammering a failing endpoint.
          setState('error');
        });
    },
    [projectId]
  );
  const reload = useCallback(() => load(genRef.current), [load]);

  useEffect(() => {
    const gen = ++genRef.current;
    // Reset on project change — never show the previous project's runs/cost
    setRuns([]);
    setListedCost(0);
    setExpanded(new Set());
    setState('loading');
    load(gen);

    return () => {
      genRef.current++;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [load, refreshKey]);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <h4 id={`runs-heading-${projectId}`} className="text-13 font-semibold text-neutral-700">
          Agent runs
        </h4>
        {state === 'ready' && runs.length > 0 && (
          <span className="text-xs text-neutral-500">
            ${listedCost.toFixed(2)} across {runs.length} listed run{runs.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {state === 'loading' ? (
        <Spinner size="sm" />
      ) : state === 'error' ? (
        <p className="text-xs text-error-600" role="alert">
          Couldn&apos;t load run history — retry by reopening this panel.
        </p>
      ) : runs.length === 0 ? (
        <p className="text-xs text-neutral-500">No agent runs recorded for this project yet.</p>
      ) : (
        <ul aria-labelledby={`runs-heading-${projectId}`} className="space-y-2">
          {tree.top.map((r) => (
            <RunRow
              key={r.id}
              run={r}
              projectId={projectId}
              nestedRuns={tree.nested.get(r.id) ?? []}
              expanded={expanded}
              toggle={toggle}
              onOpenList={onOpenList}
              onAnswered={reload}
            />
          ))}
          {tree.orphans.length > 0 && (
            <li className="pt-2">
              <p className="text-xs text-neutral-500 mb-2">
                {tree.orphans.length} node runs belong to plan runs older than the last {RUNS_LIMIT} shown.
              </p>
              <ul className="space-y-2" aria-label="Node runs from older plans">
                {tree.orphans.map((r) => (
                  <RunRow
                    key={r.id}
                    run={r}
                    projectId={projectId}
                    nestedRuns={[]}
                    expanded={expanded}
                    toggle={toggle}
                    onOpenList={onOpenList}
                    onAnswered={reload}
                  />
                ))}
              </ul>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
